export const ALL_SUBJECTS = [
  'math',
  'physics',
  'chemistry',
  'biology',
  'history',
  'geography',
  'english',
  'german',
  'french',
  'spanish',
  'computer_science',
  'social_studies',
  'other',
] as const;

export type Subject = (typeof ALL_SUBJECTS)[number];

/**
 * English glosses for each subject key. Used to guide LLM subject classification (split agent,
 * metadata) so an exam printed as "Mathematik"/"Maths" maps to the canonical key `math`. Not for UI
 * display — the UI uses the localized i18n `Subjects.*` strings.
 */
export const SUBJECT_LABELS_EN: Record<Subject, string> = {
  math: 'Mathematics',
  physics: 'Physics',
  chemistry: 'Chemistry',
  biology: 'Biology',
  history: 'History',
  geography: 'Geography',
  english: 'English',
  german: 'German',
  french: 'French',
  spanish: 'Spanish',
  computer_science: 'Computer Science',
  social_studies: 'Social Studies',
  other: 'Other — use only when the subject is none of the above',
};

/**
 * Bullet list of the allowed canonical subject keys with their English glosses, for injection into a
 * prompt that has to classify an exam's subject (the structure agent).
 */
export const SUBJECT_OPTIONS_GUIDE = (Object.entries(SUBJECT_LABELS_EN) as Array<[string, string]>)
  .map(([key, label]) => `  - ${key}: ${label}`)
  .join('\n');

/** True when `value` is exactly one of the canonical subject keys. */
export function isSubject(value: unknown): value is Subject {
  return typeof value === 'string' && (ALL_SUBJECTS as readonly string[]).includes(value);
}

/**
 * Coerce an arbitrary value to a canonical {@link Subject} key. Accepts a key directly (case-
 * insensitive) and otherwise falls back to `'other'` — so a free-text or unknown subject never
 * leaks past this boundary into storage.
 */
export function normalizeSubject(value: unknown): Subject {
  const v = String(value ?? '')
    .trim()
    .toLowerCase();
  return isSubject(v) ? v : 'other';
}
